import React, { useMemo, useState } from 'react';
import { Segmented, Tooltip } from 'antd';
import {
  FARMS_URL_PARAM_LENS,
  FARMS_URL_PARAM_TAB,
  INPUT_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  INPUT_DOC_TAB_INSIGHTS,
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
  SUMMARY_ENTITY_LABELS,
  SUMMARY_SPEND_OVER_TIME_SECTION_ID,
} from 'constants/uiConstants';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../../redux/Store';
import { useScopedInputs } from 'hooks/useScopedInputs';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { useElementWidth } from 'hooks/common/useElementWidth';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { ANALYSIS_COST_CATEGORIES } from 'pages/FarmsPage/Workspace/Insights/computeAnalysis';
import { computeCategorySpend } from 'pages/FarmsPage/Workspace/Insights/computeCategorySpend';
import {
  ACTUAL_ONLY_CATEGORIES,
  PLAN_ONLY_CATEGORIES,
} from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import SpendPieChart, { SPEND_CATEGORY_COLORS } from './SpendPieChart';
import { ChartEmptyState } from './PlanActualChartControls';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const formatDollar = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(2)}k`;
  return `${sign}$${abs.toFixed(2)}`;
};

const formatDollarFull = (v: number): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  return `${sign}$${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDollarPerHa = (v: number): string => {
  if (v <= 0) return '—';
  return formatDollar(v);
};

type SpendMetric = 'totalSpend' | 'ratePerHa';

interface MergedSpendRow {
  category: string;
  label: string;
  planTotalSpend: number;
  actualTotalSpend: number;
  planRatePerHa: number;
  actualRatePerHa: number;
}

/** One category's value for the currently selected metric, with a full-precision tooltip. */
const SpendValue: React.FC<{
  metric: SpendMetric;
  totalSpend: number;
  ratePerHa: number;
  isOverPlan?: boolean;
}> = ({ metric, totalSpend, ratePerHa, isOverPlan }) => {
  const valueClass = isOverPlan ? 'font-semibold text-danger' : undefined;
  if (metric === 'totalSpend') {
    if (totalSpend <= 0) return <span className="text-gray-300">—</span>;
    return (
      <Tooltip title={formatDollarFull(totalSpend)}>
        <span className={`cursor-default ${valueClass ?? ''}`}>{formatDollar(totalSpend)}</span>
      </Tooltip>
    );
  }
  return ratePerHa > 0 ? (
    <Tooltip title={`${formatDollarFull(ratePerHa)}/ha`}>
      <span className={`cursor-default ${valueClass ?? ''}`}>{formatDollarPerHa(ratePerHa)}</span>
    </Tooltip>
  ) : (
    <>{formatDollarPerHa(ratePerHa)}</>
  );
};

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Right-panel tab for Category Spend summary (OP-2273).
 *
 * Displays input costs broken down by category (Seed, Soil Input, Plant
 * Protection, Seed Dress, Biostimulant, Operation). CropProtection and
 * Adjuvant steps are merged under Plant Protection, matching the Summary
 * lens grouping.
 *
 * Shows Plan and Actual side by side when the panel is wide enough; below
 * RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX it collapses to a single column driven
 * by the shared Plan/Actual control at the top of the Right Panel (OP-2333 —
 * one control for every section, not one per panel). Independent of that, a
 * small metric toggle always chooses whether those columns show Total Spend
 * or $/ha — showing both metrics split by both categories at once would mean
 * five columns, too cramped even at the panel's max width. The donut's wedge
 * angles are always driven by Plan spend, with Actual progress shaded within
 * each wedge (OP-2333 — previously a Plan/Actual toggle picked which one the
 * whole donut charted; see SpendPieChart for the current design).
 */
const CategorySpendPanel: React.FC = () => {
  const { scopedInputs, isLoading: isInputsLoading } = useScopedInputs();
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const isWide = width === 0 || width >= RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;
  const narrowCategory = useSelector(
    (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
  );
  const [metric, setMetric] = useState<SpendMetric>('totalSpend');

  // "Spend over time" link (OP-2389) — the Insights Summary only has that
  // section at paddock or plan scope, so the link only shows there. It keeps
  // every other URL param (tree node, year) and drops the lens param, which
  // opens the default Summary lens.
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const showSpendOverTimeLink =
    !!activeTreeNodeKey &&
    (activeTreeNodeKey.startsWith(PADDOCK_KEY_PREFIX) ||
      activeTreeNodeKey.startsWith(INPUT_KEY_PREFIX));
  const navigate = useNavigate();
  const location = useLocation();
  const handleOpenSpendOverTime = () => {
    const next = new URLSearchParams(location.search);
    next.set(FARMS_URL_PARAM_TAB, INPUT_DOC_TAB_INSIGHTS);
    next.delete(FARMS_URL_PARAM_LENS);
    navigate({
      pathname: location.pathname,
      search: next.toString(),
      hash: SUMMARY_SPEND_OVER_TIME_SECTION_ID,
    });
  };

  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );
  const chartVisible = useSelector(
    (state: RootState) =>
      state.insightsReducer.rightPanel.chartVisible[RIGHT_PANEL_TAB_CATEGORY_SPEND]
  );
  const { data: gUnits = [], isLoading: isGUnitsLoading } = useFetchGUnits(
    activeGOrganisation || null
  );

  const isLoading = isInputsLoading || isGUnitsLoading;

  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const planResult = useMemo(
    () =>
      isLoading
        ? null
        : computeCategorySpend(scopedInputs, gUnits, PLAN_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );
  const actualResult = useMemo(
    () =>
      isLoading
        ? null
        : computeCategorySpend(scopedInputs, gUnits, ACTUAL_ONLY_CATEGORIES),
    [scopedInputs, gUnits, isLoading]
  );

  const rows = useMemo<MergedSpendRow[]>(() => {
    const planByCategory = new Map(
      (planResult?.lines ?? []).map((l) => [l.category, l])
    );
    const actualByCategory = new Map(
      (actualResult?.lines ?? []).map((l) => [l.category, l])
    );
    const merged: MergedSpendRow[] = [];
    for (const cat of ANALYSIS_COST_CATEGORIES) {
      const planLine = planByCategory.get(cat);
      const actualLine = actualByCategory.get(cat);
      if (!planLine && !actualLine) continue;
      merged.push({
        category: cat,
        label: SUMMARY_ENTITY_LABELS[cat] ?? cat,
        planTotalSpend: planLine?.totalSpend ?? 0,
        actualTotalSpend: actualLine?.totalSpend ?? 0,
        planRatePerHa: planLine?.ratePerHa ?? 0,
        actualRatePerHa: actualLine?.ratePerHa ?? 0,
      });
    }
    return merged;
  }, [planResult, actualResult]);

  const hasChartData = rows.some((r) => r.planTotalSpend > 0);

  if (isLoading) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8">
        <span className="text-base text-secondary-dark">Loading...</span>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div ref={ref} className="flex items-center justify-center py-8 text-center">
        <span className="text-base text-secondary-dark">No cost data</span>
      </div>
    );
  }

  const totalPlanSpend = rows.reduce((s, r) => s + r.planTotalSpend, 0);
  const totalActualSpend = rows.reduce((s, r) => s + r.actualTotalSpend, 0);
  const isRowOverPlan = (row: MergedSpendRow): boolean =>
    row.actualTotalSpend > 0 &&
    (row.planTotalSpend <= 0 || row.actualTotalSpend > row.planTotalSpend);

  return (
    <div ref={ref} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Segmented
          size="small"
          value={metric}
          onChange={(v) => setMetric(v as SpendMetric)}
          options={[
            { label: 'Total Spend', value: 'totalSpend' },
            { label: '$/ha', value: 'ratePerHa' },
          ]}
        />
      </div>
      <table className="w-full text-base">
        <thead>
          <tr className="border-b">
            <th className="pb-1.5 pr-3 text-left text-sm font-semibold text-secondary-dark">
              Category
            </th>
            {isWide ? (
              <>
                <th className="pb-1.5 pr-2 text-right text-sm font-semibold text-secondary-dark">
                  Plan
                </th>
                <th className="pb-1.5 text-right text-sm font-semibold text-secondary-dark">
                  Actual
                </th>
              </>
            ) : (
              <th className="pb-1.5 text-right text-sm font-semibold text-secondary-dark">
                {metric === 'totalSpend' ? 'Total Spend' : '$/ha'}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.category}
              onMouseEnter={() => setHoveredCategory(row.category)}
              onMouseLeave={() => setHoveredCategory(null)}
              className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <td className="py-1 pr-3 text-label text-charcoal">
                <span className="inline-flex items-center gap-1.5">
                  <svg width="8" height="8" aria-hidden="true" className="shrink-0">
                    <circle
                      cx="4"
                      cy="4"
                      r="3.5"
                      fill={SPEND_CATEGORY_COLORS[row.category] ?? '#9ca3af'}
                    />
                  </svg>
                  {row.label}
                </span>
              </td>
              {isWide ? (
                <>
                  <td className="py-1 pr-2 text-right tabular-nums text-charcoal">
                    <SpendValue
                      metric={metric}
                      totalSpend={row.planTotalSpend}
                      ratePerHa={row.planRatePerHa}
                    />
                  </td>
                  <td className="py-1 text-right tabular-nums text-charcoal">
                    <SpendValue
                      metric={metric}
                      totalSpend={row.actualTotalSpend}
                      ratePerHa={row.actualRatePerHa}
                      isOverPlan={isRowOverPlan(row)}
                    />
                  </td>
                </>
              ) : (
                <td className="py-1 text-right tabular-nums text-charcoal">
                  <SpendValue
                    metric={metric}
                    totalSpend={
                      narrowCategory === InputTaskCategory.Plan
                        ? row.planTotalSpend
                        : row.actualTotalSpend
                    }
                    ratePerHa={
                      narrowCategory === InputTaskCategory.Plan
                        ? row.planRatePerHa
                        : row.actualRatePerHa
                    }
                    isOverPlan={
                      narrowCategory === InputTaskCategory.Actual && isRowOverPlan(row)
                    }
                  />
                </td>
              )}
            </tr>
          ))}

          {/* ── Total row — only meaningful for Total Spend; $/ha isn't summed, matching the per-category rate's own convention. ── */}
          <tr className="border-t">
            <td className="py-1 pr-3 text-label font-semibold text-charcoal">Total</td>
            {isWide ? (
              <>
                <td className="py-1 pr-2 text-right tabular-nums font-semibold text-charcoal">
                  {metric === 'totalSpend' && (
                    <Tooltip title={formatDollarFull(totalPlanSpend)}>
                      <span className="cursor-default">
                        {formatDollar(totalPlanSpend)}
                      </span>
                    </Tooltip>
                  )}
                </td>
                <td className="py-1 text-right tabular-nums font-semibold text-charcoal">
                  {metric === 'totalSpend' && (
                    <Tooltip title={formatDollarFull(totalActualSpend)}>
                      <span
                        className={`cursor-default ${totalActualSpend > totalPlanSpend ? 'text-danger' : ''}`}
                      >
                        {formatDollar(totalActualSpend)}
                      </span>
                    </Tooltip>
                  )}
                </td>
              </>
            ) : (
              <td className="py-1 text-right tabular-nums font-semibold text-charcoal">
                {metric === 'totalSpend' && (
                  <Tooltip
                    title={formatDollarFull(
                      narrowCategory === InputTaskCategory.Plan
                        ? totalPlanSpend
                        : totalActualSpend
                    )}
                  >
                    <span
                      className={`cursor-default ${
                        narrowCategory === InputTaskCategory.Actual &&
                        totalActualSpend > totalPlanSpend
                          ? 'text-danger'
                          : ''
                      }`}
                    >
                      {formatDollar(
                        narrowCategory === InputTaskCategory.Plan
                          ? totalPlanSpend
                          : totalActualSpend
                      )}
                    </span>
                  </Tooltip>
                )}
              </td>
            )}
          </tr>
        </tbody>
      </table>
      {showSpendOverTimeLink && (
        <button
          type="button"
          onClick={handleOpenSpendOverTime}
          className="w-fit cursor-pointer border-none bg-transparent p-0 text-sm font-semibold text-primary underline hover:text-primary-dark"
        >
          Spend over time
        </button>
      )}
      {chartVisible &&
        (hasChartData ? (
          <SpendPieChart
            data={rows.map((r) => ({
              category: r.category,
              planTotalSpend: r.planTotalSpend,
              actualTotalSpend: r.actualTotalSpend,
            }))}
            hoveredCategory={hoveredCategory}
            onHoverCategoryChange={setHoveredCategory}
          />
        ) : (
          <ChartEmptyState />
        ))}
    </div>
  );
};

export default CategorySpendPanel;
